import { HandWaving, Info } from "@phosphor-icons/react";
import { cva } from "class-variance-authority";
import { clsx } from "clsx";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";

import type { Conversation } from "../../types";

interface NotificationAreaProps {
  conversation: Conversation;
  hasMessageFromAnotherUser: boolean;
}

interface NotificationProps {
  title: string;
  subtitle?: string;
  variant: "red" | "gray" | "purple";
}

const iconContainerVariants = cva(
  "mr-1 rounded-md border p-px [&_svg]:size-4",
  {
    variants: {
      variant: {
        red: "border-red-200 bg-red-50 text-red-500",
        gray: "border-black-200 bg-black-50 ",
        purple: "border-purple-200 bg-purple-50 text-purple-500",
      },
    },
  },
);

function Notification({ title, subtitle = null, variant }: NotificationProps) {
  return (
    <div
      className={clsx(
        "mx-4 mb-2 flex flex-wrap items-center justify-center gap-1 self-center rounded-2xl border bg-white px-2 py-1 shadow-sm lg:mx-0",
        variant === "red" ? "border-red-400" : "border-black-200",
      )}
    >
      <div className={iconContainerVariants({ variant })}>
        {variant === "purple" ? <HandWaving /> : <Info weight="fill" />}
      </div>

      <span className="text-xs-bold">{title}</span>

      {subtitle && (
        <span className="text-center text-xs text-black-600 lg:text-start">
          {subtitle}
        </span>
      )}
    </div>
  );
}

export default function NotificationArea({
  conversation,
  hasMessageFromAnotherUser,
}: NotificationAreaProps) {
  const {
    conversation_urn: conversationUrn,
    participants,
    last_message: lastMessage,
    owner,
    connected: isConnected,
  } = conversation;

  const { accounts } = useSelectedWorkspaceContext();
  const account = accounts.find(({ id }) => id === owner);

  const { first_name: firstName, is_lead: isLead } = participants[0] || {};
  const isMessagingBlocked =
    !hasMessageFromAnotherUser && !isLead && !isConnected;

  if (!conversationUrn && !lastMessage) {
    return (
      <Notification
        title={`This is the very beginning of your direct message history with ${firstName}. Say hi!`}
        variant="purple"
      />
    );
  }

  if (isMessagingBlocked) {
    return (
      <Notification
        title="Cannot message this user"
        subtitle="You will be able to chat with this user once they respond to your message"
        variant="gray"
      />
    );
  }

  if (lastMessage?.declined) {
    return (
      <Notification
        title="InMail Declined"
        subtitle="The recipient has declined your InMail, you cannot send them a message"
        variant="red"
      />
    );
  }

  if (account.state !== "LoggedIn") {
    return (
      <Notification
        title="Cannot send messages"
        subtitle="Reconnect your account to chat with this user"
        variant="gray"
      />
    );
  }
}
